import { Button } from '@ipfabric/ui-components/Button/Button';
import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { useApiTableDeletionHelper } from 'components/ApiTable/helpers/useApiTableDeletionHelper';
import { downloadCsv } from 'components/ApiTable/model/commands/downloadCsv';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type { TableBulkAction } from 'components/Table/model/TableModel';
import type { TableActionsItem } from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { DeviceAttributesForm } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/DeviceAttributes/DeviceAttributesForm';
import type { DeviceAttributesRowData } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/DeviceAttributes/DeviceAttributesRowData';
import { DeviceAttributesDataTransferForm } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/DeviceAttributes/dataTransfer/DeviceAttributesDataTransferForm';
import { useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { Helmet } from 'react-helmet';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { useResponsiveTableButton } from '../../../../../features/DiscoverySettingsForms/utils/useResponsiveTableButton';
import { DeviceAttributesWriteApiDoc } from './DeviceAttributesWriteApiDoc';

export const globalAttributesApiEndpoint = '/tables/global-attributes';

const renderCustomApiDoc = () => <DeviceAttributesWriteApiDoc />;

export const DeviceAttributesPage: FC = () => {
  const {
    settings: { deviceAttributes: deviceAttributesMsg },
  } = useLocale();
  const [editedAttribute, setEditedAttribute] = useState<
    DeviceAttributesRowData | 'new' | null
  >(null);
  const [showDataTransferForm, setShowDataTransferForm] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const confirmDialog = useSimpleConfirmDialog();
  const api = useApi();

  const renderDeleteButton = (
    id: string,
    handleDeleteClick: (id: string) => Promise<void>,
  ) => (
    <Button
      variant='plain'
      icon='fa-trash-can'
      onClick={() => handleDeleteClick(id)}
      size='small'
      type='button'
    />
  );
  const deletionHelper = useApiTableDeletionHelper({
    confirmDialog,
    confirmDialogMessages: deviceAttributesMsg.deleteRowsConfirmDialog,
    getApiTableModel: () => deviceAttributesApiTablePage.apiTableModel,
    deleteMultiple: (ids: string[]) =>
      api.request({
        method: 'DELETE',
        url: '/attributes/global',
        data: {
          attributes: {
            id: ids,
          },
        },
      }),
    renderDeleteButton,
  });

  const { actionItem: importExportActionItem } = useResponsiveTableButton({
    label: 'Import/Export attributes',
    icon: 'fa-file-import',
    onClick: () => setShowDataTransferForm(true),
    containerRef: tableContainerRef,
    size: 'small',
    variant: 'outline',
  });

  const bulkActions = useMemo(
    (): TableBulkAction[] => [deletionHelper.deleteBulkAction],
    [deletionHelper.deleteBulkAction],
  );

  const endpoint = useTableEndpoint({
    apiEndpoint: globalAttributesApiEndpoint,
    alternative: {
      id: 'global-device-attributes',
      columns: [
        '...',
        createFrontendOnlyColumn({
          key: 'action',
          disabledSort: true,
          title: deviceAttributesMsg.columns.action,
          webDependencies: ['id', 'hostname'],
          render: (row: DeviceAttributesRowData) => (
            <div>
              <IconButton
                variant='plain'
                icon='fa-pen'
                size='small'
                onClick={() => {
                  setEditedAttribute(row);
                }}
                type='button'
              />
              {deletionHelper.renderDeleteButton(row.id)}
            </div>
          ),
        }),
      ],
    },
  });

  const menuItems = useMemo(
    () =>
      [
        {
          standalone: true,
          elementWithKey: (
            <Button
              key='add'
              size='small'
              variant='outline'
              icon='fa-plus-circle'
              onClick={() => {
                setEditedAttribute('new');
              }}
              type='button'
            >
              {deviceAttributesMsg.addAttributeButton}
            </Button>
          ),
        } satisfies TableActionsItem,
        importExportActionItem,
      ].filter(Boolean),
    [deviceAttributesMsg.addAttributeButton, importExportActionItem],
  );

  const deviceAttributesApiTablePage = useApiTablePage({
    endpoint,
    additionalApiTableProps: {
      bulkActions,
      loading: deletionHelper.loading,
      renderCustomApiDoc,
      rowIdField: 'id',
      menuItems,
      description: (
        <>
          <span>
            Set custom attributes per device based on its serial number.
          </span>
          <br />
          <strong>The changes will be applied to future snapshots only.</strong>
        </>
      ),
    },
  });

  const exportAll = () =>
    deviceAttributesApiTablePage.apiTableModel.dispatch(downloadCsv, {
      override: {
        columns: ['sn', 'hostname', 'name', 'value'],
        filters: undefined,
        sort: undefined,
      },
    });

  const renderContent = () => {
    return (
      <div className='flex flex-col flex-1 gap' ref={tableContainerRef}>
        <div className='text-level-4 font-heading font-semibold'>
          Device attributes
        </div>
        <ApiTablePageView model={deviceAttributesApiTablePage} />
        {confirmDialog.render()}
        {editedAttribute !== null && (
          <DeviceAttributesForm
            attribute={editedAttribute === 'new' ? null : editedAttribute}
            onCancel={() => {
              setEditedAttribute(null);
            }}
            onSave={() => {
              deviceAttributesApiTablePage.apiTableModel.dispatch(
                reloadTableData,
              );
              setEditedAttribute(null);
            }}
          />
        )}
        {showDataTransferForm && (
          <DeviceAttributesDataTransferForm
            existingDeviceAttributes={
              (deviceAttributesApiTablePage.apiTableModel
                .data as DeviceAttributesRowData[]) ?? []
            }
            onClose={() => setShowDataTransferForm(false)}
            onExportClick={exportAll}
            onImportSuccess={() => {
              deviceAttributesApiTablePage.apiTableModel.dispatch(
                reloadTableData,
              );
            }}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <Helmet title={deviceAttributesMsg.title} />
      {renderContent()}
    </>
  );
};
